import authenticatedDataverseService from './authenticatedDataverseService';

const loginTrackingService = {
  /**
   * Record a login event to Dataverse
   */
  recordLogin: async (account) => {
    if (!account) return;
    return authenticatedDataverseService.recordLogin(account);
  },

  /**
   * Fetch all login records from Dataverse
   */
  fetchAllLogins: async () => {
    const result = await authenticatedDataverseService.getLoginRecords();
    if (result.success) {
      return result.data.map((record) => ({
        name: record.cr38c_name || 'Unknown',
        email: record.cr38c_email || 'Unknown',
        timestamp: record.cr38c_timestamp,
      }));
    }
    return [];
  },

  /**
   * Get logins filtered by month/year (from pre-fetched data)
   */
  getLoginsByMonth: (logins, year, month) => {
    return logins.filter((entry) => {
      const date = new Date(entry.timestamp);
      return date.getFullYear() === year && date.getMonth() === month;
    });
  },

  /**
   * Get a summary grouped by month (from pre-fetched data)
   */
  getMonthlySummary: (logins) => {
    const summary = {};

    logins.forEach((entry) => {
      const date = new Date(entry.timestamp);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!summary[key]) {
        summary[key] = { totalLogins: 0, uniqueUsers: new Set(), logins: [] };
      }
      summary[key].totalLogins += 1;
      summary[key].uniqueUsers.add(entry.email);
      summary[key].logins.push(entry);
    });

    return Object.entries(summary)
      .map(([key, data]) => ({
        month: key,
        label: new Date(key + '-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
        totalLogins: data.totalLogins,
        uniqueUsers: data.uniqueUsers.size,
        logins: data.logins,
      }))
      .sort((a, b) => b.month.localeCompare(a.month));
  },

  /**
   * Get unique users with their login counts (from pre-fetched data)
   */
  getUserSummary: (logins) => {
    const users = {};

    logins.forEach((entry) => {
      if (!users[entry.email]) {
        users[entry.email] = { name: entry.name, email: entry.email, loginCount: 0, lastLogin: entry.timestamp };
      }
      users[entry.email].loginCount += 1;
      if (entry.timestamp > users[entry.email].lastLogin) {
        users[entry.email].lastLogin = entry.timestamp;
      }
    });

    return Object.values(users).sort((a, b) => b.loginCount - a.loginCount);
  },

  /**
   * Export login data as CSV
   */
  exportCSV: (data) => {
    const headers = 'Name,Email,Login Time\n';
    const rows = data
      .map((entry) => `"${entry.name}","${entry.email}","${new Date(entry.timestamp).toLocaleString('en-GB')}"`)
      .join('\n');
    const blob = new Blob([headers + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `login-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  },
};

export default loginTrackingService;
